"use client";

// Kontakt form: photo drop zone, topic, message, name, e-mail or phone, consent, honeypot;
// inline validation with summary box, then the success card. No backend yet: nothing is sent.
import Link from "next/link";
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { contact, d, img } from "@/lib/site";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1048576;
const TYPE_OK = /^image\/(jpeg|png|heic|heif|webp)$/;
const EXT_OK = /\.(jpe?g|png|heic|heif|webp)$/i;
const PREVIEW_OK = /^image\/(jpeg|png|webp)$/;
const MAIL_OK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Pick = { id: number; file: File; url: string };
type Errs = { name: boolean; contact: false | "none" | "mail"; consent: boolean };
const noErrs: Errs = { name: false, contact: false, consent: false };

const mb = (n: number) => (n / 1048576).toFixed(1).replace(".", ",") + " MB";

function fileProblem(f: File): "type" | "big" | null {
  // some browsers report HEIC with an empty type, so the extension counts too
  if (!(TYPE_OK.test(f.type) || (!f.type && EXT_OK.test(f.name)))) return "type";
  if (f.size > MAX_BYTES) return "big";
  return null;
}

function check(form: HTMLFormElement): Errs {
  const v = (n: string) => ((form.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "").trim();
  const mail = v("email");
  const tel = v("tel");
  return {
    name: !v("name"),
    contact: !mail && !tel ? "none" : mail && !MAIL_OK.test(mail) && !tel ? "mail" : false,
    consent: !(form.elements.namedItem("datenschutz") as HTMLInputElement | null)?.checked,
  };
}

const Cross = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export default function ContactForm() {
  const [sent, setSent] = useState(false);
  return sent ? <SuccessCard onReset={() => setSent(false)} /> : <RequestForm onSent={() => setSent(true)} />;
}

function RequestForm({ onSent }: { onSent: () => void }) {
  const formRef = useRef<HTMLFormElement>(null);
  const nextId = useRef(0);
  const [files, setFiles] = useState<Pick[]>([]);
  const [errs, setErrs] = useState<Errs>(noErrs);
  const [tried, setTried] = useState(false);
  const [triedFiles, setTriedFiles] = useState(false);
  const [drag, setDrag] = useState(false);
  const [focusReq, setFocusReq] = useState(0);

  // free the preview urls when the form goes away
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => () => filesRef.current.forEach((p) => p.url && URL.revokeObjectURL(p.url)), []);

  // focus the first invalid field after a failed submit
  useEffect(() => {
    if (focusReq) formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [focusReq]);

  const tooMany = files.length > MAX_FILES;
  const badFiles = files.some((p) => fileProblem(p.file));
  const eFiles = triedFiles && (tooMany || badFiles);
  const hasErrors = errs.name || !!errs.contact || errs.consent || eFiles;

  function pickFiles(e: ChangeEvent<HTMLInputElement>) {
    const added = Array.from(e.target.files ?? []).map((file) => ({
      id: nextId.current++,
      file,
      url: PREVIEW_OK.test(file.type) ? URL.createObjectURL(file) : "",
    }));
    e.target.value = ""; // lets the same photo be picked again after removing it
    if (!added.length) return;
    setFiles((prev) => [...prev, ...added]);
    setTriedFiles(true);
  }

  function removeFile(id: number) {
    setFiles((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return prev.filter((p) => p.id !== id);
    });
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const next = check(form);
    setErrs(next);
    setTried(true);
    setTriedFiles(true);
    if (next.name || next.contact || next.consent || tooMany || badFiles) {
      setFocusReq((n) => n + 1);
      return;
    }
    const honeypot = (form.elements.namedItem("website") as HTMLInputElement | null)?.value;
    if (!honeypot) {
      // Sending goes here once the backend is decided: post the form fields and `files` (mail relay or
      // form service). On failure, stay on the form (all input is kept) and show an error instead of onSent().
    }
    onSent();
  }

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={submit}
      onChange={(e) => tried && setErrs(check(e.currentTarget))}
      className="flex flex-col gap-5 rounded-xl border border-line bg-surface p-8 max-[520px]:p-6"
    >
      <div className="flex flex-col gap-1.5">
        <h2 className="m-0 text-[26px] leading-tight">Foto schicken, Einschätzung erhalten</h2>
        <p className="m-0 text-[15px] text-ink-3">Kostenlos und unverbindlich. Wir melden uns persönlich bei Ihnen.</p>
      </div>

      {hasErrors && (
        <div className="f-sum" role="alert">
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5.5M12 16.5v.5" />
          </svg>
          <span>Fast geschafft – bitte prüfen Sie die markierten Angaben. Alles, was Sie schon eingegeben haben, bleibt erhalten.</span>
        </div>
      )}

      <label
        className={`drop${drag ? " drag" : ""}`}
        onDragEnter={() => setDrag(true)}
        onDragLeave={() => setDrag(false)}
        onDrop={() => setDrag(false)}
      >
        <input
          type="file"
          name="fotos"
          accept="image/jpeg,image/png,image/heic,image/heif,image/webp,.heic,.heif"
          multiple
          aria-invalid={eFiles ? "true" : "false"}
          aria-describedby={eFiles ? "foto-hint err-files" : "foto-hint"}
          onChange={pickFiles}
        />
        <svg aria-hidden="true" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
          <circle cx="12" cy="13.5" r="3.5" />
        </svg>
        <strong className="text-[17px]">Fotos vom Handy oder Computer auswählen</strong>
        <span id="foto-hint" className="f-hint">
          Am besten von vorne, von der Seite und ein Detail vom Bezug · bis zu 5 Bilder, je bis 10 MB
        </span>
      </label>

      {files.length > 0 && (
        <ul className="f-files" aria-label="Ausgewählte Fotos">
          {files.map((p) => {
            const problem = fileProblem(p.file);
            const note =
              problem === "type"
                ? "Dieses Format können wir nicht öffnen – bitte JPG, PNG, HEIC oder WebP"
                : problem === "big"
                  ? `${mb(p.file.size)} – zu groß, höchstens 10 MB pro Bild`
                  : mb(p.file.size);
            return (
              <li key={p.id} className={problem ? "f-file f-bad" : "f-file"}>
                <span className="f-thumb" style={p.url ? { backgroundImage: `url(${p.url})` } : undefined} />
                <span className="f-name">
                  <span>{p.file.name}</span>
                  <small>{note}</small>
                </span>
                <button type="button" className="f-x" onClick={() => removeFile(p.id)} aria-label={`${p.file.name} entfernen`}>
                  <Cross />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {eFiles && (
        <span className="f-err" id="err-files">
          {tooMany
            ? `Höchstens 5 Bilder – bitte entfernen Sie ${files.length - MAX_FILES} davon.`
            : "Ein Bild passt nicht – entfernen Sie es oder wählen Sie ein anderes aus."}
        </span>
      )}

      <label className="f-lab">
        Worum geht es?
        <select name="thema" className="f-in">
          <option>Polsterei</option>
          <option>Sonnenschutz</option>
          <option>Sonstiges</option>
        </select>
      </label>

      <label className="f-lab">
        Ihre Nachricht
        <textarea
          name="nachricht"
          rows={4}
          className="f-in resize-y"
          placeholder="Was für ein Möbelstück ist es, und was wünschen Sie sich?"
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <label className="f-lab">
          Name
          <input
            type="text"
            name="name"
            autoComplete="name"
            required
            className="f-in"
            aria-invalid={errs.name ? "true" : "false"}
            aria-describedby={errs.name ? "err-name" : undefined}
          />
        </label>
        {errs.name && (
          <span className="f-err" id="err-name">
            Bitte geben Sie Ihren Namen an, damit wir wissen, wen wir ansprechen.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-4">
          <label className="f-lab">
            E-Mail
            <input
              type="email"
              name="email"
              autoComplete="email"
              className="f-in"
              aria-invalid={errs.contact ? "true" : "false"}
              aria-describedby={errs.contact ? "hint-contact err-contact" : "hint-contact"}
            />
          </label>
          <label className="f-lab">
            Telefon
            <input
              type="tel"
              name="tel"
              autoComplete="tel"
              className="f-in"
              aria-invalid={errs.contact === "none" ? "true" : "false"}
              aria-describedby={errs.contact ? "hint-contact err-contact" : "hint-contact"}
            />
          </label>
        </div>
        <span id="hint-contact" className="f-hint">
          E-Mail oder Telefon – eins von beiden genügt, damit wir Sie erreichen.
        </span>
        {errs.contact && (
          <span className="f-err" id="err-contact">
            {errs.contact === "mail"
              ? "Die E-Mail-Adresse scheint unvollständig – bitte prüfen Sie das @ und die Endung."
              : "Bitte geben Sie eine E-Mail-Adresse oder eine Telefonnummer an, damit wir Sie erreichen."}
          </span>
        )}
      </div>

      {/* honeypot: people never see or fill it; a filled one means a bot */}
      <div className="f-hp" aria-hidden="true">
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="consent">
          <input
            type="checkbox"
            name="datenschutz"
            required
            aria-invalid={errs.consent ? "true" : "false"}
            aria-describedby={errs.consent ? "err-consent" : undefined}
          />
          <span>
            Ich bin mit der Verarbeitung meiner Daten gemäß der{" "}
            <Link href="/datenschutz/" className="text-inherit underline underline-offset-2">
              Datenschutzerklärung
            </Link>{" "}
            einverstanden. Ihre Angaben und Fotos nutzen wir nur, um Ihre Anfrage zu beantworten.
          </span>
        </label>
        {errs.consent && (
          <span className="f-err" id="err-consent">
            Bitte stimmen Sie der Datenschutzerklärung zu – sonst dürfen wir Ihre Anfrage nicht bearbeiten.
          </span>
        )}
      </div>

      <button
        type="submit"
        className="cursor-pointer self-start rounded-full border-0 bg-accent-fill px-7 py-4 text-[17px] font-semibold text-white"
      >
        Anfrage senden
      </button>
    </form>
  );
}

// Success card after a sent request: Nathalie's portrait, phone, seal, "Weitere Anfrage senden".
function SuccessCard({ onReset }: { onReset: () => void }) {
  const headRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headRef.current?.focus(), []);
  return (
    <div role="status" className="relative flex flex-col gap-5 overflow-hidden rounded-xl border border-line bg-surface p-8 max-[520px]:p-6">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="seal absolute right-[22px] bottom-[18px] bg-[#FAFAF8] opacity-90" src={d(img.seal)} alt="" width={104} height={104} />
      <div className="flex items-center gap-4">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={d(img.nathalie)} alt="" width={64} height={84} className="h-[84px] w-16 flex-none rounded-lg object-cover" />
        <div className="flex flex-col gap-1">
          <h2 ref={headRef} tabIndex={-1} className="m-0 text-[26px] leading-tight">
            Danke, Ihre Anfrage ist da.
          </h2>
          <span className="text-ink-3">Wir sehen uns Ihre Fotos an und melden uns bei Ihnen.</span>
        </div>
      </div>
      <p className="m-0 text-ink-2">
        Eilt es? Rufen Sie gern direkt an:{" "}
        <a href={contact.tel} className="font-semibold text-ink hover:text-accent">
          {contact.phone}
        </a>
      </p>
      <button
        type="button"
        onClick={onReset}
        className="relative cursor-pointer self-start rounded-full border-[1.5px] border-line-strong bg-transparent px-[22px] py-3 font-semibold text-inherit"
      >
        Weitere Anfrage senden
      </button>
    </div>
  );
}
